import React, { useMemo } from 'react';
import {
  CheckCircleRounded,
  PaymentsRounded,
  PhoneIphoneRounded,
  ReceiptLongRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CssBaseline,
  Paper,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import signupFlowSteps from './signupFlowConfig';
import SignupProgress from './signupProgress';
import SignupShell from './signupShell';
import signupTheme, { authHeadingStyles, authSubtextStyles } from './signupTheme';
import { safeReturnUrl } from './scoreCustomerSignup';

const completionContentByMethod = {
  card: {
    title: 'Purchase Submitted',
    description:
      'Your card payment details were captured and your plan purchase is now being processed.',
    secondary:
      'We will confirm the activation by email once the transaction is completed successfully.',
    Icon: PaymentsRounded,
  },
  apple_pay: {
    title: 'Apple Pay Authorized',
    description:
      'Your Apple Pay authorization was completed successfully in the simulated purchase flow.',
    secondary:
      'This frontend is now ready for the live Apple Pay handoff once the payment provider and merchant validation are connected.',
    Icon: PhoneIphoneRounded,
  },
  google_pay: {
    title: 'Google Pay Authorized',
    description:
      'Your Google Pay authorization was completed successfully in the simulated purchase flow.',
    secondary:
      'This frontend is now ready for the live Google Pay handoff once the payment provider is connected.',
    Icon: PhoneIphoneRounded,
  },
  cash: {
    title: 'Cash Payment Requested',
    description:
      'Your request for offline payment has been captured successfully.',
    secondary:
      'Our team can now follow up with the next steps for collection or manual confirmation.',
    Icon: ReceiptLongRounded,
  },
  bank_transfer: {
    title: 'Bank Transfer Requested',
    description:
      'Your purchase is recorded and ready for bank transfer follow-up.',
    secondary:
      'We can provide transfer instructions or confirmation steps in the next stage of the flow.',
    Icon: ReceiptLongRounded,
  },
  default: {
    title: 'Registration Complete',
    description:
      'Your signup flow is complete and your purchase details have been captured.',
    secondary:
      'You can now move forward with the remaining activation and confirmation steps.',
    Icon: CheckCircleRounded,
  },
};

const summaryRowStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 160px) minmax(0, 1fr)' },
  gap: { xs: 0.5, sm: 2 },
  alignItems: 'center',
};

export default function SignupComplete() {
  const navigate = useNavigate();
  const location = useLocation();

  const completionDetails = location.state || {};
  // Set by the sign-up page from its ?returnUrl= (already limited to an http(s) URL there).
  const returnUrl = safeReturnUrl(completionDetails.returnUrl);
  const methodType = completionDetails.paymentMethodType || 'default';

  const content = useMemo(
    () =>
      completionContentByMethod[methodType] || completionContentByMethod.default,
    [methodType]
  );

  const SummaryIcon = content.Icon;

  return (
    <ThemeProvider theme={signupTheme}>
      <CssBaseline />
      <SignupShell onNavigateLogin={() => navigate('/login')}>
        <Stack spacing={4} sx={{ maxWidth: 980, mx: 'auto' }}>
          <Box sx={{ width: '100%', px: { xs: 0, md: 1 }, pb: 1 }}>
            <SignupProgress steps={signupFlowSteps} activeStep={signupFlowSteps.length} />
          </Box>

          <Paper
            elevation={0}
            sx={{
              px: { xs: 2.5, sm: 6, md: 8 },
              py: { xs: 2.5, sm: 6, md: 8 },
              borderRadius: 4,
              backgroundColor: '#ffffff',
              boxShadow: '0 18px 48px rgba(28, 27, 31, 0.10)',
              border: '1px solid #E4E6F1',
            }}
          >
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
              <Stack spacing={3.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    background:
                      'linear-gradient(180deg, rgba(90, 143, 17, 0.12) 0%, rgba(90, 143, 17, 0.05) 100%)',
                    border: '1px solid rgba(90, 143, 17, 0.14)',
                  }}
                >
                  <SummaryIcon sx={{ fontSize: 42, color: 'primary.main' }} />
                </Box>

                <Stack spacing={1.25} alignItems="center">
                  <Typography
                    variant="h4"
                    sx={authHeadingStyles}
                  >
                    {content.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={authSubtextStyles}
                  >
                    {content.description}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={authSubtextStyles}
                  >
                    {content.secondary}
                  </Typography>
                  {completionDetails.approvalPending ? (
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 560, color: 'primary.main', fontWeight: 700 }}
                    >
                      Your registration is now waiting for approval by the Ecosystem Integrator you
                      chose, or by eKutir. You can log in as soon as it is approved.
                    </Typography>
                  ) : null}
                  {completionDetails.paymentMessage ? (
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 560, color: 'primary.main', fontWeight: 700 }}
                    >
                      {completionDetails.paymentMessage}
                    </Typography>
                  ) : null}
                </Stack>

                {(completionDetails.planName ||
                  completionDetails.paymentMethodName ||
                  completionDetails.totalFormatted) && (
                  <Paper
                    variant="outlined"
                    sx={{
                      width: '100%',
                      maxWidth: 560,
                      px: { xs: 2.5, sm: 3 },
                      py: 2.5,
                      borderRadius: 3,
                      borderColor: '#E4E6F1',
                      backgroundColor: '#FBFCFE',
                      textAlign: 'left',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" color="text.secondary">
                          Plan
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {completionDetails.planName || 'Selected Plan'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" color="text.secondary">
                          Billing
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {completionDetails.billingCycle || 'monthly'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Method
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {completionDetails.paymentMethodName || 'Payment'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {completionDetails.paymentStatusLabel ||
                            completionDetails.paymentStatus ||
                            'Completed'}
                        </Typography>
                      </Box>
                      <Box sx={summaryRowStyles}>
                        <Typography variant="body2" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {completionDetails.totalFormatted || '-'}
                        </Typography>
                      </Box>
                      {completionDetails.transactionId ? (
                        <Box sx={summaryRowStyles}>
                          <Typography variant="body2" color="text.secondary">
                            Transaction ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {completionDetails.transactionId}
                          </Typography>
                        </Box>
                      ) : null}
                      {completionDetails.financialSupportRequested ? (
                        <Typography
                          variant="caption"
                          sx={{ color: 'primary.main', fontWeight: 700, pt: 0.5 }}
                        >
                          Financial support application was also requested.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                )}

                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ minWidth: { xs: '100%', sm: 260 } }}
                >
                  Go To Login
                </Button>
                {returnUrl ? (
                  <Button
                    variant="text"
                    href={returnUrl}
                    sx={{ minWidth: { xs: '100%', sm: 260 } }}
                  >
                    Back to the app you came from
                  </Button>
                ) : null}
              </Stack>
            </Box>
          </Paper>
        </Stack>
      </SignupShell>
    </ThemeProvider>
  );
}
